import { ofetch } from 'ofetch';
import { log } from '@shared/log.browser.ts';
import type { TileListInitialSession } from '@tilelist/application/core/TileListInitialSession.ts';
import type { TileListPageSession } from '@tilelist/application/core/TileListSession.ts';
import { createPageSession } from '@tilelist/application/core/TileListSession.ts';
import type { ClientReloadContextProvider } from '@tilelist/application/ports/ClientReloadContextProvider.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import type { SelectionEvents } from '@tilelist/application/ports/SelectionEvents.ts';
import type { TileListPageFetcher } from '@tilelist/application/ports/TileListPageFetcher.ts';
import type { TrackingPort } from '@tilelist/application/ports/TrackingPort.ts';
import type { VariationLoader } from '@tilelist/application/ports/VariationLoader.ts';
import type { CrocotileFeature } from '@tilelist/clients/CrocotileClient.ts';
import { EvergladesClient } from '@tilelist/clients/EvergladesClient.ts';
import { FullTileListStrategy } from '@tilelist/domain/composition/strategies/FullTileListStrategy.ts';
import { LiteTileListStrategy } from '@tilelist/domain/composition/strategies/LiteTileListStrategy.ts';
import type { PageLoadStrategy } from '@tilelist/domain/composition/strategies/PageLoadStrategy.ts';
import type { TileItem } from '@tilelist/domain/composition/TileListitem.ts';
import type { TileListPageLayout } from '@tilelist/domain/composition/TileListPage.ts';
import { createCacheKeyRefresher } from '@tilelist/services/cache/CacheKeyRefresher.browser.ts';
import { restoreBySelector, saveScrollSelector } from '@tilelist/services/cache/ScrollPositionCache.browser.ts';
import { maybeRestoreFromCache, saveToCache } from '@tilelist/services/cache/TileListCache.browser.svelte.ts';
import { createEventsIncoming } from '@tilelist/services/events/EventsIncoming.browser.ts';
import { createEventsOutgoing } from '@tilelist/services/events/EventsOutgoing.browser.ts';
import type { SelectionDependencies } from '@tilelist/services/selection/NexusSelection.browser.ts';
import { createProductionSelectionDependencies } from '@tilelist/services/selection/SelectionDependencies.browser.ts';
import { createSelectionEvents } from '@tilelist/services/selection/SelectionEvents.browser.ts';
import { startSelection } from '@tilelist/services/selection/SelectionLifecycle.browser.ts';
import {
    createTopicPageSelectionNavigation,
    defaultSelectionNavigation,
} from '@tilelist/services/selection/SelectionNavigation.browser.ts';
import { getTrackingService, getVariationLoadingService } from '@tilelist/services/ServiceFactory.browser.ts';
import { trackAdjustForTileList } from '@tilelist/services/tracking/AppsTracking.browser.ts';
import { trackFirebase } from '@tilelist/services/tracking/FirebaseTracking.browser.ts';
import { frontendPageViewTracking } from '@tilelist/services/tracking/PageViewTracking.browser.ts';
import { createTrackingClientReloadContextProvider } from '@tilelist/services/TrackingClientReloadContextProvider.browser.ts';

export type StrategyFactory = (variationLoader: VariationLoader) => PageLoadStrategy;

export type TileListBrowserWiring = {
    session: TileListPageSession;
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    selectionEvents: SelectionEvents;
    trackingService: TrackingPort;
    variationLoadingService: VariationLoader;
    pageFetcher: TileListPageFetcher;
    reloadContextProvider: ClientReloadContextProvider;
    stopSelection(): void;
    restoreScrollPosition(): Promise<void>;
    saveScrollSelector(): void;
    maybeRestoreFromCache(pageView: TileListPageLayout): Promise<TileListPageLayout>;
    sendTracking(pageView: TileListPageLayout, historic?: boolean): void;
    onRedirectToPdp(item: TileItem, currentPageView: TileListPageLayout | null): void;
};

function selectionDependencies(
    initialSession: TileListInitialSession,
    trackingService: TrackingPort,
    navigation: SelectionDependencies['navigation'],
): Omit<SelectionDependencies, 'eventsOutgoing'> {
    return {
        ...createProductionSelectionDependencies(),
        trackUrl: (url) => trackingService.trackUrl(url),
        navigation,
    };
}

export function createTileListLiteBrowserWiring(
    initialSession: TileListInitialSession,
    feature: CrocotileFeature = 'tilelist-lite',
): TileListBrowserWiring {
    return createTileListBrowserWiring(
        initialSession,
        () => new LiteTileListStrategy(),
        createTopicPageSelectionNavigation(),
        feature,
    );
}

/**
 *
 *
 *
 *
 *
 */
export function createTileListBrowserWiring(
    initialSession: TileListInitialSession,
    strategyFactory?: StrategyFactory,
    navigation: SelectionDependencies['navigation'] = defaultSelectionNavigation(),
    feature: CrocotileFeature = 'tilelist',
): TileListBrowserWiring {
    const variationLoadingService = getVariationLoadingService(feature);
    const eventsOutgoing = createEventsOutgoing();
    const eventsIncoming = createEventsIncoming();
    const trackingService = getTrackingService();
    const reloadContextProvider = createTrackingClientReloadContextProvider(trackingService);

    const selectionLifecycle = startSelection({
        eventsIncoming,
        eventsOutgoing,
        selection: selectionDependencies(initialSession, trackingService, navigation),
    });
    const stopSelection = selectionLifecycle;
    const selectionEvents = createSelectionEvents(selectionLifecycle.selection);

    const evergladesClient = new EvergladesClient({
        fetchClient: ofetch,
        logger: log,
        selectionEvents,
        timeout: 10000,
    });

    const strategy = strategyFactory
        ? strategyFactory(variationLoadingService)
        : new FullTileListStrategy(variationLoadingService);

    const session = createPageSession(initialSession, {
        pageFetcher: evergladesClient,
        variationLoadingService,
        reloadContextProvider,
        cacheKeyRefresher: createCacheKeyRefresher(),
        strategy,
    });

    function sendTracking(pageView: TileListPageLayout, historic?: boolean): void {
        const pageViewTracking = frontendPageViewTracking(!!historic, selectionEvents);
        /*                                                                        */
        /*                                                     */
        trackingService.trackPageView(pageView, pageViewTracking);
        trackFirebase(eventsOutgoing, pageView.tileListItems);
        trackAdjustForTileList(pageView.tileListItems, session.onex);
    }

    function onRedirectToPdp(_item: TileItem, currentPageView: TileListPageLayout | null): void {
        saveScrollSelector();
        if (currentPageView) {
            void saveToCache(currentPageView);
        }
    }

    return {
        session,
        eventsIncoming,
        eventsOutgoing,
        selectionEvents,
        trackingService,
        variationLoadingService,
        pageFetcher: evergladesClient,
        reloadContextProvider,
        stopSelection,
        restoreScrollPosition: restoreBySelector,
        saveScrollSelector,
        maybeRestoreFromCache,
        sendTracking,
        onRedirectToPdp,
    };
}
